import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Loader2, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="h-10 w-10 rounded-xl bg-[#114A31] flex items-center justify-center">
            <Leaf className="h-5 w-5 text-white" />
          </span>
          <span className="font-heading text-xl font-semibold text-[#1C1C1C]">ScorpPath</span>
        </Link>
        <div className="bg-white border border-[#E5E5E5] rounded-2xl shadow-sm p-8">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <MailCheck className="h-12 w-12 text-[#22C55E] mx-auto" />
              <h1 className="mt-4 font-heading text-2xl font-medium text-[#1C1C1C]">Check your email</h1>
              <p className="mt-2 text-sm text-[#595D5A]">
                If an account exists for <span className="font-medium">{email}</span>, we've sent a password reset link. It expires in 1 hour.
              </p>
              <Link to="/login" className="mt-6 inline-block text-[#114A31] font-medium hover:underline" data-testid="back-to-login-link">
                Back to log in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-medium text-[#1C1C1C]">Forgot your password?</h1>
              <p className="text-sm text-[#595D5A] mt-1">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
                <div>
                  <Label htmlFor="email" className="text-[#1C1C1C]">Email</Label>
                  <Input
                    id="email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    className="mt-1.5 rounded-xl border-[#E5E5E5] py-6"
                    placeholder="you@business.com"
                  />
                </div>
                {error && <p className="text-sm text-[#EF4444]" data-testid="forgot-error">{error}</p>}
                <Button
                  type="submit" disabled={loading} data-testid="forgot-submit-btn"
                  className="w-full rounded-xl py-6 bg-[#114A31] hover:bg-[#1A6B46] text-white"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>
              <p className="mt-6 text-center text-sm text-[#595D5A]">
                Remembered it?{" "}
                <Link to="/login" className="text-[#114A31] font-medium hover:underline">Log in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
